import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, CheckCircle, X, HelpCircle, TrendingUp, Globe, MapPin, Target } from 'lucide-react';

const FIT_CONFIG = {
  likely_fit: { color: '#10B981', label: 'Likely Fit' },
  possible_fit: { color: '#10B981', label: 'Possible Fit' },
  conditional_fit: { color: '#D97706', label: 'Conditional' },
  insufficient_data: { color: '#8B827D', label: 'Needs Data' },
  unlikely: { color: '#DC2626', label: 'Unlikely' },
};

const PathwayRow = ({ result, fitKey }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg = FIT_CONFIG[fitKey] || FIT_CONFIG.insufficient_data;

  return (
    <div style={{ borderBottom: '1px solid rgba(48,43,44,0.06)' }}>
      <button
        data-testid={`pathway-row-${result.country.replace(/\s/g, '-').toLowerCase()}`}
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '16px',
          padding: '14px 20px', background: 'transparent', border: 'none',
          cursor: 'pointer', textAlign: 'left',
          transition: 'background 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(48,43,44,0.03)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <div style={{
          width: '6px', height: '6px', borderRadius: '50%',
          background: cfg.color, flexShrink: 0,
          boxShadow: `0 0 8px ${cfg.color}40`,
        }} />
        <span style={{
          fontFamily: 'var(--font-heading)', fontSize: '0.9375rem',
          fontWeight: 600, color: '#302B2C', flex: 1,
        }}>
          {result.country}
        </span>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: '0.65rem',
          color: 'var(--text-muted)', letterSpacing: '0.03em',
        }}>
          {result.track_label || ''}
        </span>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: '0.65rem',
          color: cfg.color, letterSpacing: '0.06em',
          minWidth: '80px', textAlign: 'right',
        }}>
          {cfg.label.toUpperCase()}
        </span>
        {result.score_range && (
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: '0.875rem',
            fontWeight: 600, color: '#302B2C', minWidth: '36px', textAlign: 'right',
          }}>
            {result.score_range.confirmed_min}
          </span>
        )}
        <ChevronDown
          size={14} color="var(--text-muted)"
          style={{
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s', flexShrink: 0,
          }}
        />
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ padding: '0 20px 20px' }}>
              {/* Notes */}
              <div style={{
                padding: '12px 16px', marginBottom: '16px',
                background: 'rgba(48,43,44,0.03)',
                borderRadius: '2px',
                borderLeft: `3px solid ${cfg.color}`,
              }}>
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
                  {result.notes}
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {/* Gates */}
                {result.hard_gates?.length > 0 && (
                  <div>
                    <div style={{
                      fontFamily: 'var(--font-mono)', fontSize: '0.6rem',
                      letterSpacing: '0.12em', textTransform: 'uppercase',
                      color: 'var(--text-muted)', marginBottom: '8px',
                    }}>Requirements</div>
                    {result.hard_gates.map((g, i) => (
                      <div key={g.gate} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '5px 0' }}>
                        {g.passed === true ? <CheckCircle size={13} color="#10B981" style={{ marginTop: '2px', flexShrink: 0 }} /> :
                         g.passed === false ? <X size={13} color="#DC2626" style={{ marginTop: '2px', flexShrink: 0 }} /> :
                         <HelpCircle size={13} color="#A39A94" style={{ marginTop: '2px', flexShrink: 0 }} />}
                        <div>
                          <span style={{ fontSize: '0.75rem', color: '#302B2C' }}>
                            {g.gate.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          <span style={{ display: 'block', fontSize: '0.65rem', color: 'var(--text-muted)' }}>{g.note}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Actions */}
                {result.next_actions?.length > 0 && (
                  <div>
                    <div style={{
                      fontFamily: 'var(--font-mono)', fontSize: '0.6rem',
                      letterSpacing: '0.12em', textTransform: 'uppercase',
                      color: 'var(--brand-purple)', marginBottom: '8px',
                    }}>Next Steps</div>
                    {result.next_actions.map((a, i) => (
                      <div key={`step-${a.substring(0, 20)}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '5px 0' }}>
                        <TrendingUp size={13} color="var(--brand-purple)" style={{ marginTop: '2px', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{a}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AllPathways = ({ assessment }) => {
  const { prCountries, wpCountries, conditional, total, sections } = useMemo(() => {
    const pr = assessment.pr_countries || [];
    const wp = assessment.wp_countries || [];
    const cond = assessment.conditional_opportunities || [];
    return {
      prCountries: pr,
      wpCountries: wp,
      conditional: cond,
      total: pr.length + wp.length + cond.length,
      sections: [
        { key: 'pr', label: 'Permanent Residency', items: pr, fitField: 'pr_fit' },
        { key: 'wp', label: 'Work Permits & Visas', items: wp, fitField: 'wp_fit' },
        { key: 'cond', label: 'Conditional Opportunities', items: cond, fitField: 'pr_fit' },
      ].filter(s => s.items.length > 0),
    };
  }, [assessment]);

  if (total === 0) return null;

  const sectionIcons = { pr: <Globe size={15} color="#10B981" />, wp: <MapPin size={15} color="var(--brand-purple)" />, cond: <Target size={15} color="#D97706" /> };

  return (
    <section
      data-testid="all-pathways-section"
      style={{
        padding: '0 1.5rem 4rem',
        maxWidth: '900px',
        margin: '0 auto',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.5 }}
        style={{ marginBottom: '2rem' }}
      >
        <div style={{
          fontFamily: 'var(--font-body)', fontSize: '0.7rem', fontWeight: 700,
          letterSpacing: '0.25em', textTransform: 'uppercase',
          color: '#F67425', marginBottom: '0.75rem',
        }}>
          Complete Assessment
        </div>
        <h2 style={{
          fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.5rem, 3vw, 2rem)',
          fontWeight: 800, color: '#302B2C', letterSpacing: '-0.03em',
          lineHeight: 1.05, margin: 0,
        }}>
          All countries <span style={{ background: 'linear-gradient(135deg, #F67425, #BB0518)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>assessed</span>
        </h2>
      </motion.div>

      {sections.filter(s => s.items.length > 0).map(section => (
        <motion.div
          key={section.key}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
          style={{ marginBottom: '1.5rem' }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            marginBottom: '8px', padding: '0 4px',
          }}>
            {sectionIcons[section.key]}
            <span style={{
              fontFamily: 'var(--font-heading)', fontSize: '0.9375rem',
              fontWeight: 600, color: '#302B2C',
            }}>
              {section.label}
            </span>
            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: '0.65rem',
              color: 'var(--text-muted)',
            }}>
              {section.items.length}
            </span>
          </div>
          <div style={{
            background: '#ffffff',
            border: '1px solid rgba(48,43,44,0.1)',
            borderRadius: '2px',
            overflow: 'hidden',
          }}>
            {section.items.map(r => (
              <PathwayRow
                key={`${section.key}-${r.country}`}
                result={r}
                fitKey={r[section.fitField]}
              />
            ))}
          </div>
        </motion.div>
      ))}
    </section>
  );
};

export default AllPathways;
